// Calendar dates of the browser's zone, for the period a page asks the API about.
//
// toISOString() gives the UTC day: Copenhagen's local midnight of 1 September is
// 2026-08-31T22:00:00Z, so `d.toISOString().split('T')[0]` names the day before.
// These read and build the local calendar components instead.
import { getTenantZone } from '@poiesis/locale'
import { dayInTenantZone, lastCoveredDayInTenantZone, resolvePeriod } from '@poiesis/views/period'

/** "YYYY-MM-DD" of the local calendar day `d` falls on. */
export function toLocalDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Local midnight of a "YYYY-MM-DD" string (new Date('2026-09-01') is UTC midnight, not local). */
export function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map((p) => parseInt(p, 10))
  return new Date(y, m - 1, d)
}

/**
 * FY to date: the first day of the current fiscal year through yesterday (today has no complete data), as
 * local-midnight Dates of the TENANT calendar's days. `fiscalStartMonth` is 1-12; a fiscal year that starts in
 * September runs Sep 1 - Aug 31, so on 17 Oct 2026 it started 1 Sep 2026, on 17 Aug 2026 on 1 Sep 2025.
 *
 * SEN-433 T7 (A21): one rule. The window is Poiesis's `fytd` preset (resolvePeriod, the twin of the server's
 * resolve_period) in the tenant's zone, read back as inclusive tenant-calendar days; the browser's zone no longer
 * decides which day "yesterday" is. On the fiscal year's first day the window is that day (never start after end).
 */
export function fiscalYearToDate(fiscalStartMonth: number, now: Date = new Date(), tz: string = getTenantZone()): { start: Date; end: Date } {
  const w = resolvePeriod({ kind: 'preset', preset: 'fytd' }, now, tz, fiscalStartMonth)
  return {
    start: parseLocalDate(dayInTenantZone(w.period_start, tz)),
    end: parseLocalDate(lastCoveredDayInTenantZone(w.period_end, tz)),
  }
}
